/**
 * MKS-454 — how a DERIVED invoice due date is written, wherever one is shown.
 *
 * An invoice with no `due_date` now ages from one the server derives (issue
 * date + the payment term of its projects, fallback Net 14), so the three
 * surfaces that show a due date have to show that date instead of a dash —
 * otherwise the Receivables page calls an invoice 300 days overdue next to a
 * due date of "–", which is how the number stops being believed.
 *
 * Petra's condition for deriving it at all: it must never be mistaken for a
 * date that came from accounting. So a derived date is always written with
 * its term — `14.10.2026 (derived: Net 14)` — and the SPA never derives one
 * itself: `effectiveDueDate` and `derivedNetDays` come from the server, which
 * owns the one definition (`invoiceEffectiveDueDate.ts`). Both are null while
 * `mks_454_derived_invoice_due_date` is off, and then every caller here falls
 * back to the stored due date exactly as it did before.
 */
import { formatDisplayDate } from './format';

/** The three fields the server sends about an invoice's due date. */
export interface InvoiceDueDateFields {
  /** The stored `due_date`, or null on an invoice accounting never dated. */
  dueDate: string | null;
  /** The date overdue was measured from; null while the flag is off. */
  effectiveDueDate?: string | null;
  /** The term a derived date used, in days; null when nothing was derived. */
  derivedNetDays?: number | null;
}

/** `Net 14` — the term a derived due date was built with. */
export function netTermLabel(netDays: number): string {
  return `Net ${netDays}`;
}

/** The full marker, as it reads next to a derived date. */
export function derivedDueDateMarker(netDays: number): string {
  return `(derived: ${netTermLabel(netDays)})`;
}

/** Is the date this invoice ages from one the server derived? */
export function hasDerivedDueDate(
  invoice: InvoiceDueDateFields,
): invoice is InvoiceDueDateFields & { effectiveDueDate: string; derivedNetDays: number } {
  return (
    !invoice.dueDate &&
    typeof invoice.effectiveDueDate === 'string' &&
    typeof invoice.derivedNetDays === 'number'
  );
}

/**
 * The due date to show, as one string: the stored one, a derived one with its
 * marker, or `absent` when there is neither.
 */
export function formatInvoiceDueDate(invoice: InvoiceDueDateFields, absent = '-'): string {
  if (invoice.dueDate) return formatDisplayDate(invoice.dueDate);
  if (hasDerivedDueDate(invoice)) {
    return `${formatDisplayDate(invoice.effectiveDueDate)} ${derivedDueDateMarker(invoice.derivedNetDays)}`;
  }
  return absent;
}
